<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { usePortalStore, portalAccessStatusEnum, entityChangeTypeEnum } from '@/stores/portal.store'
import { useApps } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { useChips } from '@/composables/chips'
import { useRateCard } from '@/composables/rate-card'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import StaffEdit from '@/components/shared/StaffEdit.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import PortalAccessStatusChip from '@/components/shared/PortalAccessStatusChip.vue'
import CollapsibleDetailHeader from '@/components/shared/CollapsibleDetailHeader.vue'
import DetailField from '@/components/shared/DetailField.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import AvailabilityCalendar from '@/components/shared/AvailabilityCalendar.vue'
import ExpandableContent from '@/components/shared/ExpandableContent.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import StaffMovementsPanel from '@/components/planner/StaffMovementsPanel.vue'
import { useWebSiteStore, staffDetailAllocationsFilterKey, searchResultTypeEnum } from '@/stores/web-site.store'
import { useChatStore } from '@/stores/chat.store'
import { useEntityChanges } from '@/composables/entity-changes'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  tab: {
    type: String,
    default: 'info',
  },
})
useChatStore().trackPageEntity(searchResultTypeEnum.STAFF, () => props.modelValue?.id)

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const editItem = ref(null)
const { phoneCall } = useApps()
const { notifySuccess } = useNotify()
const { getTagChips, getGroupChips, getServiceAvailabilityChip } = useChips()

const staff = ref(props.modelValue || null)
const activeTab = ref(props.tab)
let changed = false


const roleChip = computed(() => staff.value?.role != null
  ? webSiteStore.getLocalizedRoles().find(r => r.id === staff.value.role) || null
  : null)

const portalStatusChip = computed(() =>
  portalStore.getLocalizedPortalAccessStatuses().find(s => s.id === staff.value?.portal_access_status) || null)

const showPortalStatusChip = computed(() =>
  [portalAccessStatusEnum.PENDING, portalAccessStatusEnum.EXPIRED].includes(staff.value?.portal_access_status))

const showResendableStatus = computed(() =>
  !!staff.value?.id
  && showPortalStatusChip.value
  && webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS))

const isResending = ref(false)
const onResendInvite = async () => {
  isResending.value = true
  try {
    await planningStore.resendInviteStaffAsync(staff.value.id)
    staff.value.portal_access_status = portalAccessStatusEnum.PENDING
    changed = true
    notifySuccess(i18n.t('info.userActivationEmailSent'))
  } finally {
    isResending.value = false
  }
}

const rateCard = useRateCard()
const unitSymbol = computed(() => rateCard.unitSymbol(staff.value))
const vatLabel = computed(() => rateCard.vatLabel(staff.value))
const priceLabel = computed(() => rateCard.priceLabel(staff.value))
const costLabel = computed(() => rateCard.costLabel(staff.value))
const hasRateCard = computed(() => rateCard.hasRateCard(staff.value))

onMounted(async() => {
  if (!staff.value?.id) return
  if (planningStore.groups.length === 0) await planningStore.fetchGroupsAsync({ all: true }, true)
  staff.value = (await planningStore.fetchStaffByIdAsync(staff.value.id)).data
})

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => { await planningStore.archiveEquipmentAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteEquipmentAsync(id, config) },
})

const onArchive = async(item) => {
  await archiveAsync(item.id, item.archived)
  item.archived = !item.archived
}

const onNavigateBack = () => {
  changed ? emit('update') : emit('cancel')
}

const emit = defineEmits(['cancel', 'update'])
useNavigator(onNavigateBack)

const reloadStaffAsync = async() => {
  staff.value = (await planningStore.fetchStaffByIdAsync(staff.value.id)).data
  changed = true
}

const onEditItemUpdate = async() => {
  await reloadStaffAsync()
  editItem.value = null
}


useEntityChanges([entityChangeTypeEnum.STAFF], (change) => change.id === staff.value?.id && reloadStaffAsync(), () => editItem.value !== null)
</script>
<template>
  <q-card class="dialog-card-flex">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      persistent
      class="dialog-xs"
    >
      <staff-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onEditItemUpdate"
        @cancel="onEditItemUpdate"
        @delete="editItem = null; emit('update')"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <navigate-back
        :text="staff?.surname_name"
        :archived="staff?.archived"
        @cancel="onNavigateBack"
      >
        <template #right>
          <div style="display: flex; align-items: center;">
            <filter-sort-selector
              v-if="activeTab === 'allocation'"
              icon
              :icon-color="null"
              :filter-key="staffDetailAllocationsFilterKey"
            />
            <q-btn
              v-show="!staff?.archived && webSiteStore.can(webSiteStore.permissionFlagEnum.MASTER_DATA)"
              flat
              round
              icon="mdi-pencil"
              @click="editItem = staff"
            />
            <q-btn
              v-show="webSiteStore.can(webSiteStore.permissionFlagEnum.DELETE)"
              flat
              round
              :icon="staff?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
              @click="onArchive(staff)"
            />
          </div>
        </template>
      </navigate-back>
      <q-tab-panels v-model="activeTab" animated keep-alive>
        <q-tab-panel name="info" class="q-pa-none">
          <collapsible-detail-header
            v-model:compact="webSiteStore.staffDetailHeaderCompact"
            :avatar="staff?.avatar_file || staff?.avatar_url"
            avatar-icon="mdi-camera-off"
          >
            <template #chips>
              <template v-if="webSiteStore.staffDetailHeaderCompact">
                <div class="column items-end" style="gap: 0.25rem;">
                  <div v-if="staff?.phone" class="row items-center justify-end no-wrap" style="gap: 0.25rem;">
                    <q-chip
                      v-if="staff?.phone"
                      clickable
                      dense
                      size="md"
                      color="blue-grey"
                      text-color="white"
                      icon="mdi-phone"
                      :label="staff.phone"
                      @click="phoneCall(staff.phone)"
                    />
                  </div>
                  <div
                    v-if="getGroupChips(staff?.groups).length || roleChip"
                    class="row items-center justify-end"
                    style="gap: 0.25rem;"
                  >
                    <chip-template
                      v-if="getGroupChips(staff?.groups).length"
                      :values="getGroupChips(staff.groups)"
                      size="sm"
                      justify="flex-end"
                    />
                    <chip-template
                      v-if="roleChip"
                      :values="[roleChip]"
                      size="sm"
                      justify="flex-end"
                    />
                  </div>
                  <div
                    v-if="showPortalStatusChip"
                    class="row items-center justify-end"
                    style="gap: 0.25rem;"
                  >
                    <portal-access-status-chip
                      :status="staff.portal_access_status"
                      :resendable="showResendableStatus"
                      :loading="isResending"
                      @resend="onResendInvite"
                    />
                  </div>
                </div>
              </template>
              <template v-else>
                <chip-template v-if="getGroupChips(staff?.groups).length" :values="getGroupChips(staff.groups)" size="md" />
                <chip-template :values="getTagChips(staff?.tags)" size="md" />
              </template>
            </template>
            <template #info>
              <q-list class="q-px-sm">
                <q-item>
                  <q-item-section>
                    <div class="row items-start" style="column-gap: 1rem;">
                      <detail-field class="col" :label="i18n.t('common.code')" :value="staff.code" />
                      <detail-field class="col" right :label="i18n.t('common.role')">
                        <chip-template v-if="roleChip" :values="[roleChip]" justify="flex-end" />
                        <template v-else>-</template>
                      </detail-field>
                    </div>
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <div class="row items-start" style="column-gap: 1rem;">
                      <detail-field class="col" :label="i18n.t('common.email')">
                        <a v-if="staff.email" :href="`mailto:${staff.email}`" class="text-primary">{{ staff.email }}</a>
                        <template v-else>-</template>
                      </detail-field>
                      <detail-field
                        class="col"
                        right
                        :label="i18n.t('common.phone')"
                        :value="staff.phone"
                      />
                    </div>
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <div class="row items-start" style="column-gap: 1rem;">
                      <detail-field class="col" :label="i18n.t('common.workplace')" :value="staff.warehouse_name" />
                      <detail-field class="col" right :label="i18n.t('common.availabilityType')">
                        <chip-template v-if="staff.availability_type != null" :values="[getServiceAvailabilityChip(staff)]" justify="flex-end" />
                        <template v-else>-</template>
                      </detail-field>
                    </div>
                  </q-item-section>
                </q-item>
                <q-item>
                  <q-item-section>
                    <div class="row items-start" style="column-gap: 1rem;">
                      <detail-field class="col" :label="i18n.t('common.portalAccessStatus')">
                        <portal-access-status-chip
                          v-if="portalStatusChip"
                          :status="staff.portal_access_status"
                          :resendable="showResendableStatus"
                          :loading="isResending"
                          @resend="onResendInvite"
                        />
                        <template v-else>-</template>
                      </detail-field>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </collapsible-detail-header>
          <q-list v-if="hasRateCard" class="q-px-sm">
            <q-item>
              <q-item-section>
                <div class="row items-start" style="column-gap: 1rem;">
                  <detail-field class="col" :label="i18n.t('common.price')" :value="priceLabel ? (unitSymbol ? `${priceLabel} / ${unitSymbol}` : priceLabel) : null" />
                  <detail-field
                    class="col"
                    right
                    :label="i18n.t('common.cost')"
                    :value="costLabel ? (unitSymbol ? `${costLabel} / ${unitSymbol}` : costLabel) : null"
                  />
                </div>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section>
                <div class="row items-start" style="column-gap: 1rem;">
                  <detail-field class="col" :label="i18n.t('common.unitOfMeasure')" :value="unitSymbol" />
                  <detail-field
                    class="col"
                    right
                    :label="i18n.t('common.vat')"
                    :value="vatLabel"
                  />
                </div>
              </q-item-section>
            </q-item>
          </q-list>
          <div class="q-px-sm q-pb-sm">
            <expandable-content
              always-show-toggle
              :expand-label="i18n.t('common.showAvailability')"
              :collapse-label="i18n.t('common.hideAvailability')"
            >
              <availability-calendar :service="staff" />
            </expandable-content>
          </div>
          <template v-if="staff.service_files?.length">
            <q-separator class="q-my-sm" />
            <q-card-section class="q-py-sm">
              <q-item-label header class="q-pa-none q-mb-sm">
                {{ i18n.t('common.attachment', staff.service_files.length) }}
              </q-item-label>
              <file-viewer
                v-for="file in staff.service_files"
                :key="file.id"
                :mobile="isMobile"
                hide-delete
                :cell-info="{ value: [file] }"
                @download="async(e) => await planningStore.downloadServiceFileAsync(e.id, e.name)"
              />
            </q-card-section>
          </template>
        </q-tab-panel>

        <q-tab-panel name="holidays" class="q-pa-none">
          <staff-movements-panel :staff="staff" tab="holidays" />
        </q-tab-panel>

        <q-tab-panel name="allocation" class="q-pa-none">
          <staff-movements-panel :staff="staff" tab="allocation" />
        </q-tab-panel>
      </q-tab-panels>
    </q-card-section>
    <q-separator />
    <q-tabs
      v-model="activeTab"
      align="justify"
      active-color="primary"
      indicator-color="primary"
      class="text-grey"
    >
      <q-tab name="info" :label="i18n.t('common.info')" />
      <q-tab name="holidays" :label="i18n.t('common.staff.holidays')" />
      <q-tab name="allocation" :label="i18n.t('common.staff.allocation')" />
    </q-tabs>
  </q-card>
</template>